import { AppLoader } from './app-loader'
import {
	useAppLoaderActions,
	useAppLoaderIsLoading,
	useAppLoaderShowProgress,
} from './app-loader-store'

import type { PropsWithChildren } from 'react'

/**
 * Renders the global {@link AppLoader} driven by the vanilla
 * {@link appLoaderStore}. Wrap the app once near the root.
 *
 * The state lives in a vanilla Zustand store rather than React state so that
 * non-React callers (router `beforeLoad`, etc.) can show/hide the loader.
 * `useAppLoader` is kept as a React hook for ergonomic call sites.
 */
export const AppLoaderProvider = ({ children }: PropsWithChildren) => {
	const isLoading = useAppLoaderIsLoading()
	const showProgress = useAppLoaderShowProgress()

	return (
		<>
			<AppLoader isVisible={isLoading} showProgress={showProgress} />
			{children}
		</>
	)
}

/**
 * Hook exposing imperative AppLoader controls. Same shape as the previous
 * context API — `showLoader`, `hideLoader`, `showProgress(boolean)`.
 *
 * For tenant switches, prefer letting the `_dashboard` `beforeLoad`
 * orchestrate the loader (see tenant-switching docs). This hook is for
 * route-specific flows (auth bootstrap, signup pages, onboarding).
 */
export const useAppLoader = () => {
	const { showLoader, hideLoader, setShowProgress } = useAppLoaderActions()

	return {
		showLoader,
		hideLoader,
		showProgress: setShowProgress,
	}
}
